import React, { useEffect, useState } from "react";
import {
  Box, TextField, MenuItem, Button, Alert, CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import api from "../../utils/api";

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };
const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];

// Green Sheet / Work Order creation fields, shared between the standalone
// /home/workorders/new page and the Dashboard "New Request" modal.
const NewWorkOrderForm = ({ propertyId, onSuccess, onCancel }) => {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    description: "",
    details: "",
    workorder_type_id: "",
    priority: "Low",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/workorder-types").then((res) => setTypes(res.data || [])).catch(() => {});
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!propertyId) { setErr("Select a property/member first."); return; }
    if (!form.description.trim()) { setErr("Description is required."); return; }

    setSaving(true);
    try {
      const res = await api.post("/addWorkOrder", {
        property_id: propertyId,
        description: form.description.trim(),
        details: form.details.trim() || null,
        workorder_type_id: form.workorder_type_id || null,
        priority: form.priority,
      });
      onSuccess?.(res.data?.workOrderId);
    } catch (error) {
      setErr(error.response?.data?.message || "Failed to create work order.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}

      <TextField fullWidth label="Description *" value={form.description} onChange={set("description")}
        sx={{ ...fieldSx, mb: 2 }} />

      <TextField fullWidth multiline rows={3} label="Details" value={form.details} onChange={set("details")}
        sx={{ ...fieldSx, mb: 2 }} />

      <TextField select fullWidth label="Type" value={form.workorder_type_id} onChange={set("workorder_type_id")}
        sx={{ ...fieldSx, mb: 2 }}>
        <MenuItem value="">Unspecified</MenuItem>
        {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>)}
      </TextField>

      <TextField select fullWidth label="Priority" value={form.priority} onChange={set("priority")}
        sx={{ ...fieldSx, mb: 3 }}>
        {PRIORITY_OPTIONS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
      </TextField>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button type="submit" variant="contained" disabled={saving || !propertyId}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon />}
          sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, boxShadow: "none",
            backgroundColor: "#2f63ad", "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" } }}>
          Create Green Sheet
        </Button>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
        )}
      </Box>
    </form>
  );
};

export default NewWorkOrderForm;
